import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkout } from '../context/WorkoutContext';
import ExerciseDisplay from '../components/ExerciseDisplay';
import './Workout.css';

const Workout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, actions, computed } = useWorkout();
  const {
    generatedWorkout,
    currentExerciseIndex,
    totalCompletedReps,
    totalRemainingReps,
    exerciseReps,
    repCount,
    setCount
  } = state;

  const [exerciseCompleted, setExerciseCompleted] = useState(false);

  // Get trainer program info from location state
  const isTrainerProgram = location.state?.isTrainerProgram || false;
  const trainerName = location.state?.trainerName;
  const programName = location.state?.programName;
  const intensityLevel = location.state?.intensityLevel || 100;
  const intensityLabel = location.state?.intensityLabel || 'Advanced';

  // Store trainer program info in sessionStorage for WorkoutComplete to access
  useEffect(() => {
    if (isTrainerProgram) {
      const trainerProgramInfo = {
        isTrainerProgram,
        trainerName,
        programName,
        intensityLevel,
        intensityLabel
      };
      sessionStorage.setItem('trainerProgramInfo', JSON.stringify(trainerProgramInfo));
    }
  }, [isTrainerProgram, trainerName, programName, intensityLevel, intensityLabel]);

  // If no workout has been generated or started, redirect to summary
  useEffect(() => {
    if (generatedWorkout.length === 0) {
      navigate('/workout-summary');
    }
  }, [generatedWorkout, navigate]);

  const currentExercise = computed.currentExercise;
  const isLastExercise = computed.isLastExercise;
  const isFirstExercise = computed.isFirstExercise;

  // Get the current exercise rep information
  const currentExerciseReps = exerciseReps[currentExercise] || {
    totalReps: repCount,
    remainingReps: repCount,
    completedReps: 0
  };

  // Reset exercise completion status when exercise changes
  useEffect(() => {
    setExerciseCompleted(false);
  }, [currentExerciseIndex]);

  // Calculate progress percentage with trainer program logic
  const calculateProgress = () => {
    let totalReps;

    if (isTrainerProgram && setCount > 1) {
      // For trainer programs, the total is simply repCount * generatedWorkout.length
      // because generatedWorkout already includes all exercises across all sets
      totalReps = repCount * generatedWorkout.length;
    } else {
      // For regular workouts, calculate as before
      totalReps = generatedWorkout.reduce((sum, exercise) => {
        return sum + (exerciseReps[exercise]?.totalReps || repCount);
      }, 0);
    }

    return Math.round((totalCompletedReps / totalReps) * 100);
  };

  // Calculate total remaining reps correctly for trainer programs
  const getTotalRemainingReps = () => {
    if (isTrainerProgram && setCount > 1) {
      const totalReps = repCount * generatedWorkout.length;
      return totalReps - totalCompletedReps;
    }
    return totalRemainingReps;
  };

  return (
      <div className="workout-container">
        <header className="workout-header">
          <button
              className="back-button"
              onClick={() => {
                if (window.confirm('Going back will pause your workout. Continue?')) {
                  navigate('/workout-summary');
                }
              }}
          >
            Back
          </button>
          <h1>Workout in Progress</h1>
          {isTrainerProgram && (
              <div className="trainer-program-badge">
                <p>{programName} by {trainerName}</p>
              </div>
          )}
        </header>

        <div className="progress-bar-container">
          <div
              className="progress-bar"
              style={{ width: `${calculateProgress()}%` }}
          ></div>
          <div className="progress-text">
            {calculateProgress()}% Complete
          </div>
        </div>

        <div className="exercise-stats">
          <div className="stat-row">
            <div className="stat-item">
              <span className="stat-label">Current Exercise:</span>
              <span className="stat-value">{currentExerciseIndex + 1}/{generatedWorkout.length}</span>
            </div>

            <div className="stat-item">
              <span className="stat-label">Target Reps:</span>
              <span className="stat-value">{repCount}</span>
            </div>

            <div className="stat-item">
              <span className="stat-label">Completed Reps:</span>
              <span className="stat-value">{totalCompletedReps}</span>
            </div>

            <div className="stat-item">
              <span className="stat-label">Remaining Reps:</span>
              <span className="stat-value">{getTotalRemainingReps()}</span>
            </div>
          </div>

          {isTrainerProgram && setCount > 1 && (
              <div className="trainer-info">
                <div className="stat-item">
                  <span className="stat-label">Sets:</span>
                  <span className="stat-value">{setCount}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Reps:</span>
                  <span className="stat-value">{repCount * generatedWorkout.length}</span>
                </div>
              </div>
          )}
        </div>

        <div className="exercise-display-container">
          <ExerciseDisplay
              exerciseName={currentExercise}
              targetReps={repCount}
              completedReps={currentExerciseReps.completedReps}
          />
        </div>
      </div>
  );
};

export default Workout;